/**
 * useQuizAnswers - Answer Submission and Selection Management
 * 
 * Handles:
 * - Selected option(s) state (single and multiple choice)
 * - Answer submission to Firebase via unifiedDataService
 * - Score and streak calculation
 * - Question tracking for stats
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { RealtimeDBService } from '../../../firebase/unifiedDataService';
import { recordQuizParticipation } from '../../../utils/studentStatsService';
import { recordQuestionStats } from '../../../utils/questionStatsService';
import { Logger } from '../../../utils/logger';
import { storage } from '../../../services/storageService';

const log = Logger.create('useQuizAnswers');

/**
 * Custom hook for quiz answer management
 * @param {string} instancePath - The full Firebase path for the quiz instance
 * @param {string} participantId - The participant's ID
 * @param {Object} currentQuestion - The current question object
 * @param {number} currentQuestionIndex - The current question index
 * @param {Object} settings - Quiz settings
 * @param {boolean} timerExpired - Whether the timer has expired
 * @param {boolean} showCorrectAnswer - Whether correct answer is being revealed (triggers point award)
 * @param {string} studentId - Optional student ID for stats tracking
 * @param {Array} hiddenOptions - Indices of options hidden by 50:50 joker
 * @returns {Object} Answer state and actions
 */
export function useQuizAnswers(
  instancePath,
  participantId,
  currentQuestion,
  currentQuestionIndex,
  settings = {},
  timerExpired = false,
  showCorrectAnswer = false,
  studentId = null,
  hiddenOptions = []
) {
  // Selection state
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  
  // Score tracking
  const [totalScore, setTotalScore] = useState(0);
  const [lastPointsEarned, setLastPointsEarned] = useState(null);
  const [pointsAwarded, setPointsAwarded] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  
  // Question timing
  const [questionShownAt, setQuestionShownAt] = useState(null);
  
  // Stats tracking
  const [statsRecorded, setStatsRecorded] = useState(false);
  const [answersMap, setAnswersMap] = useState({}); // Track all answers for final stats

  // Restore answersMap and check submission state on mount and question change
  useEffect(() => {
    if (instancePath && participantId) {
      const storageKey = `quiz_answers_${instancePath}_${participantId}`;
      try {
        const stored = storage.get(storageKey);
        if (stored) {
          setAnswersMap(stored);
        }
      } catch (e) {
        log.warn('Failed to restore answersMap from storage:', e);
      }
    }
  }, [instancePath, participantId]);

  // Check if current question already has a submitted answer
  useEffect(() => {
    if (!instancePath || !participantId || currentQuestionIndex === null) return;

    const checkSubmissionState = async () => {
      try {
        const answerPath = `${instancePath}/answers/${currentQuestionIndex}/${participantId}`;
        const existingAnswer = await RealtimeDBService.get(answerPath);
        
        if (existingAnswer && existingAnswer.selectedIndices) {
          log.debug('Found existing answer for current question, restoring submitted state');
          setSelectedOptions(existingAnswer.selectedIndices);
          setSubmitted(true);
          
          // Also restore from answersMap if available for points
          if (answersMap[currentQuestionIndex]) {
            setLastPointsEarned(answersMap[currentQuestionIndex].points);
          }
        }
      } catch (error) {
        log.error('Error checking submission state:', error);
      }
    };

    checkSubmissionState();
  }, [instancePath, participantId, currentQuestionIndex]);

  // Check if points were already awarded for current question on mount
  useEffect(() => {
    if (!instancePath || currentQuestionIndex === null) return;

    const checkPointsAwarded = async () => {
      try {
        const pointsAwardedPath = `${instancePath}/points_awarded/${currentQuestionIndex}`;
        const alreadyAwarded = await RealtimeDBService.get(pointsAwardedPath);
        
        if (alreadyAwarded?.awarded) {
          log.debug(`Points already awarded for question ${currentQuestionIndex} on mount, marking as awarded`);
          setPointsAwarded(true);
        }
      } catch (error) {
        log.error('Error checking if points already awarded on mount:', error);
      }
    };

    checkPointsAwarded();
  }, []); // Only run once on mount

  // Reset state when question changes
  // But ONLY after points have been awarded (or if never submitted)
  const prevQuestionIndexRef = useRef(currentQuestionIndex);
  useEffect(() => {
    // If question index changed
    if (prevQuestionIndexRef.current !== currentQuestionIndex) {
      // Force award points for previous question if not yet awarded
      if (submitted && !pointsAwarded && lastPointsEarned !== null) {
        const isCorrect = lastPointsEarned > 0;
        if (isCorrect) {
          setCurrentStreak(prev => {
            const newStreak = prev + 1;
            setMaxStreak(max => Math.max(max, newStreak));
            return newStreak;
          });
          setTotalScore(prev => prev + lastPointsEarned);
        } else {
          setCurrentStreak(0);
        }
      }

      // Now reset for new question
      setSelectedOptions([]);
      setSubmitted(false);
      setLastPointsEarned(null);
      // DON'T reset pointsAwarded here - will be set by checkPointsAwarded
      setQuestionShownAt(Date.now());
      
      prevQuestionIndexRef.current = currentQuestionIndex;
      
      // Check if points were already awarded for this new question
      if (instancePath) {
        const checkPointsAwarded = async () => {
          try {
            const pointsAwardedPath = `${instancePath}/points_awarded/${currentQuestionIndex}`;
            const alreadyAwarded = await RealtimeDBService.get(pointsAwardedPath);
            
            if (alreadyAwarded?.awarded) {
              log.debug(`Points already awarded for question ${currentQuestionIndex}, marking as awarded`);
              setPointsAwarded(true);
            } else {
              setPointsAwarded(false);
            }
          } catch (error) {
            log.error('Error checking if points already awarded:', error);
            setPointsAwarded(false);
          }
        };
        checkPointsAwarded();
      }
    }
  }, [currentQuestionIndex, submitted, pointsAwarded, lastPointsEarned, instancePath]);

  // Realtime listener for points_awarded - decoupled from showCorrectAnswer animation
  // This allows animation and points calculation to work independently
  useEffect(() => {
    if (!instancePath || currentQuestionIndex === null || currentQuestionIndex < 0) return;
    
    const pointsAwardedPath = `${instancePath}/points_awarded/${currentQuestionIndex}`;
    
    log.debug(`Setting up points_awarded listener for question ${currentQuestionIndex}`);
    
    // Subscribe to points_awarded changes
    const unsubscribe = RealtimeDBService.subscribe(pointsAwardedPath, (data) => {
      if (data?.awarded && submitted && !pointsAwarded && lastPointsEarned !== null) {
        log.info(`Points awarded signal received for question ${currentQuestionIndex}, awarding ${lastPointsEarned} points`);
        setPointsAwarded(true);
        
        const isCorrect = lastPointsEarned > 0;
        
        if (isCorrect) {
          setCurrentStreak(prev => {
            const newStreak = prev + 1;
            setMaxStreak(max => Math.max(max, newStreak));
            return newStreak;
          });
          setTotalScore(prev => prev + lastPointsEarned);
        } else {
          setCurrentStreak(0);
        }
      } else if (data?.awarded && !pointsAwarded) {
        // Points awarded but no answer submitted - just mark as awarded
        log.debug(`Points awarded for question ${currentQuestionIndex} but no answer submitted`);
        setPointsAwarded(true);
      }
    });
    
    return () => {
      log.debug(`Cleaning up points_awarded listener for question ${currentQuestionIndex}`);
      if (unsubscribe) unsubscribe();
    };
  }, [instancePath, currentQuestionIndex, submitted, pointsAwarded, lastPointsEarned]);

  // Remove selected options that are hidden by 50:50 joker
  useEffect(() => {
    if (hiddenOptions.length > 0 && selectedOptions.length > 0) {
      const filteredOptions = selectedOptions.filter(opt => !hiddenOptions.includes(opt));
      
      // Only update if something actually changed
      if (filteredOptions.length !== selectedOptions.length) {
        log.info(`50:50 Joker: Removing ${selectedOptions.length - filteredOptions.length} hidden options from selection`);
        setSelectedOptions(filteredOptions);
      }
    }
  }, [hiddenOptions, selectedOptions]);

  /**
   * Handle option selection (single or multiple choice)
   */
  const handleSelectOption = useCallback((index) => {
    if (submitted || timerExpired) return;

    // Check for multiple choice using correctAnswerIndices (the standard)
    const correctIndices = currentQuestion?.correctAnswerIndices || [0];
    const isMultiple = currentQuestion?.allowMultiple || correctIndices.length > 1;

    if (isMultiple) {
      // Toggle selection for multiple choice
      setSelectedOptions(prev =>
        prev.includes(index)
          ? prev.filter(i => i !== index)
          : [...prev, index]
      );
    } else {
      // Single selection - replace
      setSelectedOptions([index]);
    }
  }, [currentQuestion, submitted, timerExpired]);

  /**
   * Calculate points for an answer
   */
  const calculatePoints = useCallback((isCorrect, responseTimeMs) => {
    if (!settings.enableGamification || !isCorrect) return 0;

    let points = settings.basePointsPerQuestion || 100;

    // Time bonus (faster = more points, up to 50% bonus)
    if (settings.enableTimeBonus && responseTimeMs) {
      const maxTime = (settings.questionTimerSeconds || 30) * 1000;
      const timeBonus = Math.max(0, 1 - (responseTimeMs / maxTime)) * 0.5;
      points = Math.round(points * (1 + timeBonus));
    }

    // Streak bonus
    if (settings.enableStreakBonus && currentStreak > 0) {
      const streakBonus = Math.min(currentStreak, 5) * (settings.streakBonusPoints || 50);
      points += streakBonus;
    }

    return points;
  }, [settings, currentStreak]);

  /**
   * Check if answer is correct
   * Questions should be normalized via normalizeQuestion() before reaching here
   * but we keep a simple fallback just in case
   */
  const checkAnswer = useCallback((selectedIndices) => {
    if (!currentQuestion) return false;

    // Use correctAnswerIndices (the standard) - questions should already be normalized
    const correctIndices = currentQuestion.correctAnswerIndices || [0];

    if (correctIndices.length === 0) {
      log.warn('No valid correctIndices found!');
      return false;
    }

    // For multiple choice, all correct answers must be selected
    if (currentQuestion.allowMultiple || correctIndices.length > 1) {
      if (selectedIndices.length !== correctIndices.length) return false;
      return correctIndices.every(idx => selectedIndices.includes(idx)) &&
             selectedIndices.every(idx => correctIndices.includes(idx));
    }

    // For single choice
    return selectedIndices.length === 1 && 
           correctIndices.includes(selectedIndices[0]);
  }, [currentQuestion]);

  /**
   * Submit answer to Firebase
   */
  const submitAnswer = useCallback(async () => {
    if (!instancePath || !participantId || selectedOptions.length === 0 || submitted) {
      return;
    }

    const responseTimeMs = questionShownAt ? Date.now() - questionShownAt : null;
    const isCorrect = checkAnswer(selectedOptions);
    const points = calculatePoints(isCorrect, responseTimeMs);

    // Update local state - but DON'T award points yet (wait for showResults)
    setSubmitted(true);
    // Store points for later reveal, but don't show them yet
    setLastPointsEarned(points);
    setPointsAwarded(false); // ❌ NOT awarded yet! Will be set to true when admin shows results

    // DON'T update totalScore or streak yet - wait for admin to reveal answer
    // This prevents students from seeing if they're correct immediately

    // Track answer for stats using state update (immutable)
    const newAnswersMap = {
      ...answersMap,
      [currentQuestionIndex]: {
        selectedOptions,
        isCorrect,
        points,
        responseTimeMs
      }
    };
    setAnswersMap(newAnswersMap);

    // Save to localStorage for persistence across page reloads
    try {
      const storageKey = `quiz_answers_${instancePath}_${participantId}`;
      storage.set(storageKey, newAnswersMap);
    } catch (e) {
      log.warn('Failed to save answersMap to storage:', e);
    }

    try {
      // Submit answer to Firebase using RealtimeDBService
      if (!instancePath) {
        log.error('No instancePath provided for answer submission');
        return;
      }

      // PUBLIC answer path - only selectedIndex/indices and timing
      const publicAnswerPath = `${instancePath}/answers/${currentQuestionIndex}/${participantId}`;
      const publicAnswerData = {
        selectedIndex: selectedOptions.length === 1 ? selectedOptions[0] : null,
        selectedIndices: selectedOptions,
        submittedAt: Date.now(),
        responseTimeMs: responseTimeMs
      };
      
      // PRIVATE validation path - includes isCorrect and pointsEarned (owner-only readable)
      const validationPath = `${instancePath}/answer_validation/${currentQuestionIndex}/${participantId}`;
      const validationData = {
        ...publicAnswerData,
        isCorrect: isCorrect,
        pointsEarned: points
      };
      
      // Write to both paths
      await Promise.all([
        RealtimeDBService.set(publicAnswerPath, publicAnswerData),
        RealtimeDBService.set(validationPath, validationData)
      ]);

      // Update participant score (write to participants, not answers)
      if (settings.enableGamification) {
        const participantPath = `${instancePath}/participants/${participantId}`;
        await RealtimeDBService.update(participantPath, {
          totalScore: totalScore + points,
          currentStreak: isCorrect ? currentStreak + 1 : 0,
          lastAnswerAt: Date.now()
        });
      }

    } catch (error) {
      log.error('Error submitting answer:', error);
    }
  }, [
    instancePath,
    participantId,
    selectedOptions,
    submitted,
    currentQuestionIndex,
    questionShownAt,
    checkAnswer,
    calculatePoints,
    settings,
    totalScore,
    currentStreak
  ]);

  /**
   * Record student stats at end of quiz
   */
  const recordStats = useCallback(async (templateId, templateName, totalQuestions, courseId = null, courseName = null, teacherUserId = null) => {
    if (statsRecorded || !studentId) return;

    try {
      const answers = Object.values(answersMap);
      const correctCount = answers.filter(a => a.isCorrect).length;
      const totalPoints = answers.reduce((sum, a) => sum + a.points, 0);
      
      // Calculate percentage score
      const percentageScore = totalQuestions > 0 
        ? Math.round((correctCount / totalQuestions) * 100) 
        : 0;

      // Extract just the instanceId from the full path (quiz_instances/{userId}/{instanceId})
      // This is used as the sessionId/document key in Firestore/RTDB
      const pathParts = instancePath ? instancePath.split('/') : [];
      const sessionId = pathParts.length >= 3 ? pathParts[2] : instancePath;

      // Record overall quiz participation stats
      await recordQuizParticipation({
        studentId,
        sessionId, // Just the instanceId, not the full path
        quizName: templateName,
        courseId,
        courseName,
        teacherUserId,
        totalQuestions,
        correctAnswers: correctCount,
        score: percentageScore,
        points: totalPoints
      });
      
      // Record per-question stats for adaptive practice mode
      if (courseId) {
        log.debug('Recording question stats for practice mode...');
        const questionsData = await RealtimeDBService.get(`${instancePath}/questions`);
        
        if (questionsData) {
          const questionPromises = Object.entries(answersMap).map(async ([questionIndex, answer]) => {
            const question = questionsData[questionIndex];
            if (!question) return;
            
            try {
              await recordQuestionStats({
                studentId,
                question: {
                  question: question.question,
                  options: question.options,
                  correctAnswerIndex: question.correctAnswerIndex,
                  correctAnswerIndices: question.correctAnswerIndices,
                  allowMultiple: question.allowMultiple
                },
                courseId,
                sourceQuizId: sessionId,
                sourceQuizName: templateName,
                isCorrect: answer.isCorrect,
                timeSpent: answer.timeSpent || 0
              });
            } catch (err) {
              log.error(`Error recording stats for question ${questionIndex}:`, err);
            }
          });
          
          await Promise.all(questionPromises);
          log.success('Question stats recorded successfully');
        }
      }

      setStatsRecorded(true);
      log.info(`Quiz stats recorded for student ${studentId}: ${correctCount}/${totalQuestions} correct, ${totalPoints} points`);
    } catch (error) {
      log.error('Error recording student stats:', error);
    }
  }, [studentId, instancePath, answersMap, statsRecorded]);

  /**
   * Initialize score from storage
   */
  const initializeScore = useCallback((storedScore, storedStreak, storedMaxStreak) => {
    setTotalScore(storedScore || 0);
    setCurrentStreak(storedStreak || 0);
    setMaxStreak(storedMaxStreak || 0);
  }, []);

  return {
    // Selection state
    selectedOptions,
    setSelectedOptions,
    handleSelectOption,
    
    // Submission state
    submitted,
    submitAnswer,
    
    // Score state
    totalScore,
    lastPointsEarned,
    pointsAwarded,
    currentStreak,
    maxStreak,
    
    // Helpers
    checkAnswer,
    calculatePoints,
    initializeScore,
    
    // Stats
    recordStats,
    statsRecorded,
    answersMap, // Now a state, not a ref
  };
}

export default useQuizAnswers;
